import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ACTIVITY_EVENTS,
  ACTIVITY_THROTTLE_MS,
  LAST_ACTIVITY_KEY,
  SESSION_TICK_MS,
  SessionPolicy,
} from '@nxsys/constants/session';

/** The stored moment of last activity, or null when there is nothing usable. */
function readStoredActivity(): number | null {
  try {
    const raw = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
    return Number.isFinite(raw) && raw > 0 ? raw : null;
  } catch {
    return null;
  }
}

/**
 * As above, but treating an unreadable stamp as "active now". Used on the tick,
 * where storage disappearing mid-session must not be what signs a user out.
 */
function readLastActivity(): number {
  return readStoredActivity() ?? Date.now();
}

function writeLastActivity(at: number): void {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(at));
  } catch {
    // Storage can be unavailable (private mode, quota). The in-memory ref below
    // still drives this tab; only cross-tab sharing is lost.
  }
}

type IdleTimerOptions = {
  /** Idle timers only run for a signed-in user. */
  enabled: boolean;
  /**
   * How long the window is and when to warn. Passed in rather than imported so
   * the server stays the single source of truth: the values arrive from
   * /app-configs and can change without rebuilding the client.
   */
  policy: SessionPolicy;
  /** Fired once, when the idle window closes. */
  onIdle: () => void;
  /** Fired on activity, throttled — used to renew the token. */
  onActivity?: () => void;
};

type IdleTimerState = {
  /** True from the 29-minute mark until sign-out or the next activity. */
  isWarning: boolean;
  /** Whole seconds left before sign-out. Only meaningful while warning. */
  secondsRemaining: number;
  /** Counts the current moment as activity and reopens the full window. */
  reset: () => void;
};

/**
 * Signs a user out after a period with no activity (NX-5238).
 *
 * Deadlines are derived from a stored timestamp rather than from a decrementing
 * counter, for two reasons. Browsers throttle timers in background tabs, so a
 * counter would run slow and an idle user could outlive their session; and the
 * timestamp lives in localStorage, so activity in one tab keeps every other tab
 * alive — which is what stops an active user in a second tab being signed out.
 */
export function useIdleTimer({
  enabled,
  policy,
  onIdle,
  onActivity,
}: IdleTimerOptions): IdleTimerState {
  const { idleMs, warningMs } = policy;
  const [isWarning, setIsWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(
    Math.ceil(warningMs / 1000),
  );

  // Held in refs so the effect below does not re-subscribe its listeners every
  // time a caller passes a new inline function.
  const onIdleRef = useRef(onIdle);
  const onActivityRef = useRef(onActivity);
  onIdleRef.current = onIdle;
  onActivityRef.current = onActivity;

  const lastThrottledWrite = useRef(0);
  const hasSignedOut = useRef(false);

  const markActivity = useCallback(() => {
    const now = Date.now();
    // Throttle the write, not the reset: recording once a second is enough to
    // keep the deadline moving and keeps pointer moves off the storage path.
    if (now - lastThrottledWrite.current < ACTIVITY_THROTTLE_MS) return;
    lastThrottledWrite.current = now;
    writeLastActivity(now);
    onActivityRef.current?.();
  }, []);

  const reset = useCallback(() => {
    const now = Date.now();
    lastThrottledWrite.current = now;
    writeLastActivity(now);
    setIsWarning(false);
    setSecondsRemaining(Math.ceil(warningMs / 1000));
    // Renew explicitly rather than leaving it to the DOM listener that happens
    // to see the same click. A reset arrives when the deadline is closest, so
    // "stay signed in" has to move the server's deadline too — otherwise the
    // client window reopens while the token quietly expires seconds later, and
    // the user is bounced right after asking to stay.
    onActivityRef.current?.();
  }, [warningMs]);

  useEffect(() => {
    if (!enabled) {
      setIsWarning(false);
      // Armed again for the next session, not for the next effect run. This
      // effect re-runs whenever the policy changes, and /app-configs can resolve
      // just after a sign-out — resetting there let `onIdle` fire a second time
      // and dispatch a second `logout()` (Victor, review of #1753). `enabled`
      // only goes false once the session is actually gone, which is the one
      // moment the guard should be lifted.
      hasSignedOut.current = false;
      return;
    }

    // A stamp already in storage belongs to a session that is still open — this
    // tab before it was closed, or another tab still running. Inherit it rather
    // than granting a fresh window on mount, or the timeout could be sidestepped
    // by closing the tab and reopening it: the token outlives the idle window on
    // purpose, so a reopen 35 minutes later would otherwise walk straight back
    // in. Signing out clears storage, so a stamp cannot outlive its own session
    // and be held against whoever logs in next.
    const storedAt = readStoredActivity();
    if (storedAt === null) {
      // Nothing to inherit, so the window starts now. It has to be written
      // rather than left empty: with no stamp, a user who signs in and walks
      // away without ever moving the mouse would never look idle.
      writeLastActivity(Date.now());
    } else if (Date.now() - storedAt >= idleMs) {
      // The window closed while nothing was mounted to notice. Checked against
      // the guard rather than just setting it: this branch is also what a
      // re-run lands on after a sign-out that has not yet cleared storage — the
      // policy from /app-configs arriving a moment later — and firing again is a
      // second `logout()` dispatch (Victor, review of #1753).
      if (!hasSignedOut.current) {
        hasSignedOut.current = true;
        onIdleRef.current();
      }
      return;
    }
    // Let the next real activity record itself at once instead of waiting out
    // the throttle.
    lastThrottledWrite.current = 0;

    // Bubble phase. Every event in the list reaches window on its own, and the
    // capture phase must not be used here: it would also deliver the focus the
    // warning dialog puts on its own container, which reopens the very window
    // the dialog is counting down.
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, markActivity, { passive: true }),
    );

    const interval = window.setInterval(() => {
      const idleFor = Date.now() - readLastActivity();
      const msLeft = idleMs - idleFor;

      if (msLeft <= 0) {
        if (hasSignedOut.current) return;
        hasSignedOut.current = true;
        setIsWarning(false);
        onIdleRef.current();
        return;
      }

      if (msLeft <= warningMs) {
        setIsWarning(true);
        setSecondsRemaining(Math.ceil(msLeft / 1000));
      } else {
        // Activity in another tab can pull us back out of the warning.
        setIsWarning((warning) => (warning ? false : warning));
      }
    }, SESSION_TICK_MS);

    return () => {
      window.clearInterval(interval);
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, markActivity),
      );
    };
  }, [enabled, idleMs, warningMs, markActivity]);

  return { isWarning, secondsRemaining, reset };
}

export default useIdleTimer;
